/**
 * Composable for post CRUD operations
 * Consolidates post create, read, update, delete logic across components
 */

import { ref, computed } from 'vue';
import { useAmplifyClient } from './useAmplifyClient.js';
import { useAsyncState, useAsyncList } from './useAsyncState.js';
import { POST_QUERIES } from '../graphql/queries.js';
import { POST_MUTATIONS } from '../graphql/mutations.js';
import { UI_CONFIG } from '../config/constants.js';

// Helper function to extract S3 path from profile picture URL
function getProfilePicturePath(profilePicUrl) {
  if (!profilePicUrl) return null;
  
  // If it's already an S3 path, return it
  if (profilePicUrl.startsWith('public/profile-pictures/')) {
    return profilePicUrl;
  }
  
  // If it's a signed URL, extract the path
  if (profilePicUrl.includes('amazonaws.com') && profilePicUrl.includes('public/profile-pictures/')) {
    try {
      const urlParts = profilePicUrl.split('.amazonaws.com/')[1];
      const s3Path = urlParts.split('?')[0]; // Remove query params
      return s3Path;
    } catch (error) {
      return null;
    }
  }
  
  // For external URLs (OAuth providers), store the full URL
  return profilePicUrl;
}

/**
 * Main post management composable
 */
export function usePostManagement(options = {}) {
  const {
    enableOptimisticUpdates = true,
    defaultPageSize = UI_CONFIG.DEFAULT_PAGE_SIZE,
    autoRefresh = false
  } = options;

  const { client, authenticatedClient, getUserInfo } = useAmplifyClient();
  
  // State for posts list
  const postsState = useAsyncList([]);
  
  // State for individual operations
  const createState = useAsyncState();
  const updateState = useAsyncState();
  const deleteState = useAsyncState();

  /**
   * Load posts with optional filters
   */
  const loadPosts = async (filters = {}) => {
    return await postsState.execute(async () => {
      try {
        let query, variables, dataPath;

        // Determine which query to use based on filter
        const filter = filters.filter || {};

        if (filter.type?.eq) {
          // Use FAST GSI query for main feed (by type) - no nested data
          query = POST_QUERIES.LIST_POSTS_BY_TYPE_FAST;
          variables = {
            type: filter.type.eq,
            limit: filters.limit || defaultPageSize,
            nextToken: filters.nextToken,
            sortDirection: filters.sortDirection || 'DESC'
          };
          dataPath = 'listPostByTypeAndCreatedAt';
        } else if (filter.neighborhood?.eq) {
          // Use FAST GSI query for neighborhood feed - no nested data
          query = POST_QUERIES.LIST_POSTS_BY_NEIGHBORHOOD_FAST;
          variables = {
            neighborhood: filter.neighborhood.eq,
            limit: filters.limit || defaultPageSize,
            nextToken: filters.nextToken,
            sortDirection: filters.sortDirection || 'DESC'
          };
          dataPath = 'listPostByNeighborhoodAndCreatedAt';
        } else if (filter.owner?.eq) {
          // Use FAST GSI query for user feed - no nested data
          query = POST_QUERIES.LIST_POSTS_BY_OWNER_FAST;
          variables = {
            owner: filter.owner.eq,
            limit: filters.limit || defaultPageSize,
            nextToken: filters.nextToken,
            sortDirection: filters.sortDirection || 'DESC'
          };
          dataPath = 'listPostByOwnerAndCreatedAt';
        } else {
          // Fallback to FAST standard listPosts - no nested data
          query = POST_QUERIES.LIST_POSTS_FAST;
          variables = {
            limit: filters.limit || defaultPageSize,
            nextToken: filters.nextToken,
            filter: filters.filter
          };
          dataPath = 'listPosts';
        }

        // Check if user is logged in to use appropriate client
        const { user } = await getUserInfo();
        const targetClient = user ? authenticatedClient : client;

        const result = await targetClient.graphql({
          query,
          variables
        });

        // Return full pagination data structure
        // Note: images and comments will be loaded separately by PostCard
        return {
          items: result.data[dataPath].items,
          nextToken: result.data[dataPath].nextToken,
          total: null // GraphQL doesn't typically return total count
        };
      } catch (error) {
        console.error('GraphQL Posts Error:', error);
        if (error.errors) {
          console.error('GraphQL Error Details:', error.errors);
          error.errors.forEach((err, index) => {
            console.error(`Error ${index + 1}:`, err);
          });
        }
        throw error;
      }
    });
  };

  /**
   * Load posts for a specific user
   */
  const loadUserPosts = async (userId) => {
    const filter = {
      authorId: { eq: userId }
    };

    return await loadPosts({ filter });
  };

  /**
   * Load posts for a specific neighborhood
   */
  const loadNeighborhoodPosts = async (neighborhoodPath) => {
    const filter = {
      neighborhood: { eq: neighborhoodPath }
    };

    return await loadPosts({ filter });
  };

  /**
   * Create a new post
   */
  const createPost = async (postData) => {
    const mutation = POST_MUTATIONS.CREATE_POST;

    const result = await createState.execute(async () => {
      // Get current user info for the post
      const { user, attributes } = await getUserInfo();
      
      if (!user) {
        throw new Error('Must be authenticated to create posts');
      }

      // Get proper user display name using userUtils
      const { getUserName } = await import('../utils/userUtils.js');
      const displayName = getUserName({ attributes });

      // Basic input validation
      const content = postData.content.trim();
      if (!content || content.length === 0) {
        throw new Error('Post content cannot be empty');
      }
      if (content.length > UI_CONFIG.POST_CONTENT_MAX_LENGTH) {
        throw new Error(`Post content is too long (maximum ${UI_CONFIG.POST_CONTENT_MAX_LENGTH} characters)`);
      }

      // Use name attribute as authorId if available, otherwise fall back to userId
      // This allows user profile URLs to use friendly usernames instead of UUIDs
      const authorId = attributes?.name || user.userId || user.username;

      const input = {
        content,
        title: postData.title || 'Community Update',
        author: postData.author || displayName,
        authorId: authorId,
        authorProfilePic: getProfilePicturePath(postData.authorProfilePic || attributes?.picture || attributes?.profile_picture),
        neighborhood: postData.neighborhood || null,
        owner: user.userId, // Set owner to authenticated user's UUID for authorization
        ...postData // Allow override of any fields
      };

      const response = await authenticatedClient.graphql({
        query: mutation,
        variables: { input }
      });

      return response.data.createPost;
    });

    // Optimistic update - add to local list immediately
    if (result && enableOptimisticUpdates) {
      postsState.addItem(result);
    }

    return result;
  };

  /**
   * Update an existing post
   */
  const updatePost = async (postId, updates) => {
    const mutation = `
      mutation UpdatePost($input: UpdatePostInput!) {
        updatePost(input: $input) {
          id
          title
          content
          author
          authorId
          authorProfilePic
          neighborhood
          upvoteCount
          downvoteCount
          createdAt
          updatedAt
        }
      }
    `;

    const result = await updateState.execute(async () => {
      // Get the existing post first
      const existingPost = postsState.data.value?.find(p => p.id === postId);
      
      if (!existingPost) {
        throw new Error('Post not found in local state');
      }

      // Ensure we have proper ownership - don't use dangerous fallbacks
      if (!existingPost.owner) {
        throw new Error('Post missing owner field - cannot update for security reasons');
      }

      const input = {
        id: postId,
        ...updates,
        // Preserve required fields for authorization
        title: updates.title || existingPost.title,
        author: existingPost.author,
        authorId: existingPost.authorId,
        owner: existingPost.owner // Must exist, no fallback
      };

      const response = await authenticatedClient.graphql({
        query: mutation,
        variables: { input }
      });

      return response.data.updatePost;
    });

    // Optimistic update - update in local list
    if (result && enableOptimisticUpdates) {
      postsState.updateItem(postId, result);
    }

    return result;
  };

  /**
   * Delete a post
   */
  const deletePost = async (postId, confirmCallback = null) => {
    // Optional confirmation
    if (confirmCallback && !await confirmCallback()) {
      return null; // User cancelled
    }

    const mutation = `
      mutation DeletePost($input: DeletePostInput!) {
        deletePost(input: $input) {
          id
        }
      }
    `;

    const result = await deleteState.execute(async () => {
      const response = await authenticatedClient.graphql({
        query: mutation,
        variables: {
          input: { id: postId }
        }
      });

      return response.data.deletePost;
    });

    // Optimistic update - remove from local list
    if (result && enableOptimisticUpdates) {
      postsState.removeItem(postId);
    }

    return result;
  };

  /**
   * Built-in confirmation dialog for deletes
   */
  const deletePostWithConfirm = async (postId, customMessage = null) => {
    const message = customMessage || 'Are you sure you want to delete this post? This action cannot be undone.';
    
    return await deletePost(postId, () => {
      return new Promise(resolve => {
        resolve(confirm(message));
      });
    });
  };

  /**
   * Refresh posts list
   */
  const refreshPosts = async () => {
    return await loadPosts();
  };

  /**
   * Search posts by content
   */
  const searchPosts = async (searchTerm) => {
    const filter = {
      or: [
        { content: { contains: searchTerm } },
        { title: { contains: searchTerm } }
      ]
    };

    return await loadPosts({ filter });
  };

  /**
   * Sort posts in local state
   */
  const sortPosts = (sortFn) => {
    postsState.sortList(sortFn);
  };

  /**
   * Sort by creation date (newest first)
   */
  const sortByNewest = () => {
    sortPosts((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  /**
   * Sort by creation date (oldest first)
   */
  const sortByOldest = () => {
    sortPosts((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  };

  // Computed properties for convenience
  const posts = computed(() => postsState.data.value || []);
  const postsLoading = computed(() => postsState.loading.value);
  const postsError = computed(() => postsState.error.value);
  const postsCount = computed(() => posts.value.length);
  const isEmpty = computed(() => postsCount.value === 0 && !postsLoading.value);

  const isCreating = computed(() => createState.loading.value);
  const isUpdating = computed(() => updateState.loading.value);
  const isDeleting = computed(() => deleteState.loading.value);
  const isBusy = computed(() => isCreating.value || isUpdating.value || isDeleting.value);

  const hasErrors = computed(() => 
    postsState.hasError.value || 
    createState.hasError.value || 
    updateState.hasError.value || 
    deleteState.hasError.value
  );

  const allErrors = computed(() => [
    postsState.error.value,
    createState.error.value,
    updateState.error.value,
    deleteState.error.value
  ].filter(Boolean));

  return {
    // Data
    posts,
    postsCount,
    isEmpty,
    
    // Loading states
    postsLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isBusy,
    
    // Error states
    postsError,
    hasErrors,
    allErrors,
    
    // CRUD operations
    loadPosts,
    loadUserPosts,
    loadNeighborhoodPosts,
    createPost,
    updatePost,
    deletePost,
    deletePostWithConfirm,
    
    // Utility operations
    refreshPosts,
    searchPosts,
    sortPosts,
    sortByNewest,
    sortByOldest,
    
    // Direct state access (for advanced usage)
    postsState,
    createState,
    updateState,
    deleteState
  };
}

/**
 * Specialized composable for editing posts
 */
export function usePostEditor() {
  const editingPostId = ref(null);
  const editingContent = ref('');
  const originalContent = ref('');

  const isEditing = computed(() => editingPostId.value !== null);
  const hasChanges = computed(() => editingContent.value !== originalContent.value);

  const startEdit = (post) => {
    editingPostId.value = post.id;
    editingContent.value = post.content;
    originalContent.value = post.content;
  };

  const cancelEdit = () => {
    editingPostId.value = null;
    editingContent.value = '';
    originalContent.value = '';
  };

  const isEditingPost = (postId) => {
    return editingPostId.value === postId;
  };

  return {
    editingPostId: computed(() => editingPostId.value),
    editingContent: computed({
      get: () => editingContent.value,
      set: (value) => editingContent.value = value
    }),
    isEditing,
    hasChanges,
    
    startEdit,
    cancelEdit,
    isEditingPost
  };
}